import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApp } from '../../context/AppContext'
import './Results.css'

export default function Results() {
  const { profile } = useApp()
  const navigate = useNavigate()
  const [showCards, setShowCards] = useState(false)
  const [animatedScor, setAnimatedScor] = useState(0)

  useEffect(() => {
    if (!profile) return
    // Animate score
    const target = profile.gravitate?.scor || 0
    let current = 0
    const step = Math.max(1, Math.floor(target / 40))
    const interval = setInterval(() => {
      current += step
      if (current >= target) {
        current = target
        clearInterval(interval)
      }
      setAnimatedScor(current)
    }, 30)

    setTimeout(() => setShowCards(true), 800)
    return () => clearInterval(interval)
  }, [profile])

  if (!profile) return null

  const { gravitate, profil_financiar, slot_config, context } = profile
  const nivel = gravitate?.nivel || 'mediu'
  const cheltuieli = profil_financiar?.cheltuieli || {}

  const nivelColor = {
    scazut: 'nivel-scazut',
    mediu: 'nivel-mediu',
    ridicat: 'nivel-ridicat',
    critic: 'nivel-critic',
  }

  const nivelLabel = {
    scazut: 'Risc Scăzut',
    mediu: 'Risc Mediu',
    ridicat: 'Risc Ridicat',
    critic: 'Risc Critic',
  }

  const pierderiLunare = (slot_config?.miza_per_spin || 2) * (slot_config?.spinuri_disponibile_azi || 15) * 30

  return (
    <div className="results-page">
      <div className="results-container">
        <div className="results-header">
          <div className="res-logo">STOP <span>PACANELE</span></div>
          <p className="res-subtitle">Analiza ta personalizată</p>
        </div>

        {/* SCOR PRINCIPAL */}
        <div className="res-score-section">
          <div className={`res-score-ring ${nivelColor[nivel]}`}>
            <div className="res-score-num">{animatedScor}</div>
            <div className="res-score-max">/ 100</div>
          </div>
          <div className={`res-nivel-badge ${nivelColor[nivel]}`}>
            {nivelLabel[nivel]}
          </div>
          <p className="res-descriere">{gravitate?.descriere}</p>
        </div>

        {/* CARDURI DETALII */}
        <div className={`res-cards ${showCards ? 'visible' : ''}`}>
          {/* Profil financiar */}
          <div className="res-card">
            <div className="res-card-icon">💰</div>
            <h3>Profilul tău financiar</h3>
            <div className="res-card-grid">
              <div className="res-stat">
                <span className="res-stat-val">{profil_financiar?.venit_lunar?.toLocaleString()} lei</span>
                <span className="res-stat-lbl">Venit lunar</span>
              </div>
              <div className="res-stat">
                <span className="res-stat-val">{Math.round(profil_financiar?.buget_zilnic_total || 0)} lei</span>
                <span className="res-stat-lbl">Buget zilnic</span>
              </div>
              <div className="res-stat">
                <span className="res-stat-val">{Math.round(slot_config?.buget_sesiune_azi || 0)} lei</span>
                <span className="res-stat-lbl">Buget joc / zi</span>
              </div>
              <div className="res-stat">
                <span className="res-stat-val danger">{pierderiLunare.toLocaleString()} lei</span>
                <span className="res-stat-lbl">Pierderi estimate / lună</span>
              </div>
            </div>
          </div>

          {/* Cheltuieli */}
          <div className="res-card">
            <div className="res-card-icon">📊</div>
            <h3>Banii tăi zilnici</h3>
            <div className="res-expense-list">
              <ExpenseRow label="Mâncare" value={cheltuieli.mancare_zilnic} icon="🍞" />
              <ExpenseRow label="Utilități" value={cheltuieli.utilitati_zilnic} icon="💡" />
              {cheltuieli.rata_zilnic > 0 && (
                <ExpenseRow label="Rată / Chirie" value={cheltuieli.rata_zilnic} icon="🏠" />
              )}
              {cheltuieli.copii_zilnic > 0 && (
                <ExpenseRow label="Copii" value={cheltuieli.copii_zilnic} icon="👶" />
              )}
              <ExpenseRow label="Transport" value={cheltuieli.transport_zilnic} icon="🚌" />
              <ExpenseRow label="Altele" value={cheltuieli.alte_zilnic} icon="📦" />
            </div>
            <div className="res-expense-total">
              <span>Total cheltuieli / zi</span>
              <span className="res-expense-total-val">
                {Math.round(Object.values(cheltuieli).reduce((s, v) => s + (v || 0), 0))} lei
              </span>
            </div>
          </div>

          {/* Simulator config */}
          <div className="res-card">
            <div className="res-card-icon">🎰</div>
            <h3>Simulatorul tău personalizat</h3>
            <p className="res-card-desc">
              Pe baza profilului tău, simulatorul va folosi sumele tale reale
              ca să îți arate exact ce pierzi la fiecare spin.
            </p>
            <div className="res-card-grid">
              <div className="res-stat">
                <span className="res-stat-val">{slot_config?.miza_per_spin} lei</span>
                <span className="res-stat-lbl">Miză per spin</span>
              </div>
              <div className="res-stat">
                <span className="res-stat-val">{slot_config?.spinuri_disponibile_azi}</span>
                <span className="res-stat-lbl">Spinuri / zi</span>
              </div>
              <div className="res-stat">
                <span className="res-stat-val">{Math.round((slot_config?.probabilitate_castig || 0.08) * 100)}%</span>
                <span className="res-stat-lbl">Șansă de câștig</span>
              </div>
              <div className="res-stat">
                <span className="res-stat-val danger">92%</span>
                <span className="res-stat-lbl">Pierzi pe termen lung</span>
              </div>
            </div>
          </div>

          {/* Context personal */}
          <div className="res-card res-card-context">
            <div className="res-card-icon">📋</div>
            <h3>Ce știm despre tine</h3>
            <div className="res-tags">
              {context?.ani_de_joc > 0 && (
                <span className="res-tag">{context.ani_de_joc} ani de joc</span>
              )}
              {context?.are_copii && <span className="res-tag">Are copii</span>}
              {context?.are_rata && <span className="res-tag">Plătește rată</span>}
              {context?.are_partener && <span className="res-tag">Are partener/ă</span>}
              {context?.a_imprumutat_inainte && <span className="res-tag warning">A împrumutat bani pt joc</span>}
              {context?.a_incercat_sa_se_opreasca && <span className="res-tag">A încercat să se oprească</span>}
            </div>
          </div>
        </div>

        {/* MESAJ MOTIVATIONAL */}
        <div className={`res-cta ${showCards ? 'visible' : ''}`}>
          <div className="res-cta-msg">
            <span className="res-cta-icon">💪</span>
            <p>Faptul că ești aici înseamnă că ai făcut deja primul pas. Programul nostru te va ghida zi de zi.</p>
          </div>
          <button className="btn-primary btn-lg btn-full" onClick={() => navigate('/dashboard')}>
            Continuă → Intră în aplicație
          </button>
          <p className="res-cta-note">Calendar, jurnal și educație — gratuit. Simulatorul terapeutic: 34,99 lei/lună.</p>
        </div>
      </div>
    </div>
  )
}

function ExpenseRow({ label, value, icon }) {
  return (
    <div className="res-expense-row">
      <span className="res-expense-icon">{icon}</span>
      <span className="res-expense-label">{label}</span>
      <span className="res-expense-val">{Math.round(value || 0)} lei / zi</span>
    </div>
  )
}
